export const captureScreenshot = async (): Promise<Blob | null> => {
	try {
		const html2canvas = (await import('html2canvas')).default;
		const canvas = await html2canvas(document.body, {
			ignoreElements: (el) => el.hasAttribute('data-feedback-widget'),
			useCORS: true,
			logging: false,
		});
		return await new Promise<Blob | null>((resolve) => {
			canvas.toBlob((blob) => resolve(blob), 'image/png');
		});
	} catch {
		console.warn('Screenshot capture failed');
		return null;
	}
};

export const createScreenRecorder = () => {
	let mediaRecorder: MediaRecorder | null = null;
	let stream: MediaStream | null = null;
	let chunks: Blob[] = [];
	let maxTimer: ReturnType<typeof setTimeout> | null = null;
	let pendingResolve: ((blob: Blob | null) => void) | null = null;
	let endedCallback: ((blob: Blob | null) => void) | null = null;

	const cleanup = () => {
		if (maxTimer) {
			clearTimeout(maxTimer);
			maxTimer = null;
		}
		if (stream) {
			stream.getTracks().forEach((t) => t.stop());
			stream = null;
		}
		mediaRecorder = null;
	};

	const buildBlob = () => (chunks.length > 0 ? new Blob(chunks, { type: 'video/webm' }) : null);

	const start = async (maxDurationMs = 60_000): Promise<void> => {
		chunks = [];
		stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
		mediaRecorder = new MediaRecorder(stream);

		mediaRecorder.ondataavailable = (e) => {
			if (e.data.size > 0) chunks.push(e.data);
		};

		mediaRecorder.onstop = () => {
			cleanup();
			const blob = buildBlob();
			if (pendingResolve) {
				pendingResolve(blob);
				pendingResolve = null;
			} else {
				endedCallback?.(blob);
			}
		};

		// Auto-stop when user clicks "Stop sharing" in browser chrome
		stream.getVideoTracks()[0]?.addEventListener('ended', () => {
			if (mediaRecorder?.state === 'recording') mediaRecorder.stop();
		});

		mediaRecorder.start();
		maxTimer = setTimeout(() => {
			if (mediaRecorder?.state === 'recording') mediaRecorder.stop();
		}, maxDurationMs);
	};

	const stop = (): Promise<Blob | null> =>
		new Promise((resolve) => {
			if (!mediaRecorder || mediaRecorder.state === 'inactive') {
				cleanup();
				resolve(buildBlob());
				return;
			}
			pendingResolve = resolve;
			mediaRecorder.stop();
		});

	const isRecording = () => mediaRecorder?.state === 'recording';

	const onEnded = (cb: (blob: Blob | null) => void) => {
		endedCallback = cb;
	};

	return { start, stop, isRecording, onEnded };
};

export const createAudioRecorder = () => {
	let mediaRecorder: MediaRecorder | null = null;
	let stream: MediaStream | null = null;
	let chunks: Blob[] = [];
	let maxTimer: ReturnType<typeof setTimeout> | null = null;
	let pendingResolve: ((blob: Blob | null) => void) | null = null;
	let endedCallback: ((blob: Blob | null) => void) | null = null;

	const cleanup = () => {
		if (maxTimer) {
			clearTimeout(maxTimer);
			maxTimer = null;
		}
		if (stream) {
			stream.getTracks().forEach((t) => t.stop());
			stream = null;
		}
		mediaRecorder = null;
	};

	const buildBlob = () => (chunks.length > 0 ? new Blob(chunks, { type: 'audio/webm' }) : null);

	const start = async (maxDurationMs = 120_000): Promise<void> => {
		chunks = [];
		stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		mediaRecorder = new MediaRecorder(stream);

		mediaRecorder.ondataavailable = (e) => {
			if (e.data.size > 0) chunks.push(e.data);
		};

		mediaRecorder.onstop = () => {
			cleanup();
			const blob = buildBlob();
			if (pendingResolve) {
				pendingResolve(blob);
				pendingResolve = null;
			} else {
				endedCallback?.(blob);
			}
		};

		mediaRecorder.start();
		maxTimer = setTimeout(() => {
			if (mediaRecorder?.state === 'recording') mediaRecorder.stop();
		}, maxDurationMs);
	};

	const stop = (): Promise<Blob | null> =>
		new Promise((resolve) => {
			if (!mediaRecorder || mediaRecorder.state === 'inactive') {
				cleanup();
				resolve(buildBlob());
				return;
			}
			pendingResolve = resolve;
			mediaRecorder.stop();
		});

	const isRecording = () => mediaRecorder?.state === 'recording';

	const onEnded = (cb: (blob: Blob | null) => void) => {
		endedCallback = cb;
	};

	return { start, stop, isRecording, onEnded };
};
